import React from 'react';
import { CheckIcon } from '@/components/UI/icons';
import { cn } from '@/utils/cn';

export interface OnboardingStep {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface OnboardingStepNavProps {
  heading: string;
  subtitle?: string;
  steps: OnboardingStep[];
  /** The id of the currently active step; earlier steps render as completed. */
  activeId: string;
}

/**
 * Step list rendered as the body of the shared {@link LeftSidePanel} for onboarding flows
 * (company setup + per-user onboarding). It is content only — the panel chrome (gradient,
 * logo, glow) comes from LeftSidePanel so onboarding matches the rest of the app. Rows use the
 * same pill shape and `chrome` tokens as the app sidebar; finished steps swap their icon for a tick.
 */
const OnboardingStepNav: React.FC<OnboardingStepNavProps> = ({ heading, subtitle, steps, activeId }) => {
  const activeIndex = steps.findIndex((s) => s.id === activeId);

  return (
    <div className="text-chrome-text">
      <div className="mb-6 px-2">
        <h2 className="text-h3 leading-snug mb-1">{heading}</h2>
        {subtitle && <p className="text-chrome-text2 text-xs">{subtitle}</p>}
      </div>

      <ol className="space-y-1">
        {steps.map((step, idx) => {
          const isActive = step.id === activeId;
          const isDone = idx < activeIndex;
          return (
            <li
              key={step.id}
              aria-current={isActive ? 'step' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-full px-3.5 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-white/[0.12] font-semibold text-chrome-text'
                  : isDone
                  ? 'font-medium text-chrome-text2'
                  : 'font-medium text-chrome-icon',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center',
                  isActive && 'text-chrome-primary',
                  isDone && 'text-chrome-accent',
                )}
              >
                {isDone ? <CheckIcon size={16} /> : step.icon}
              </span>
              <span className="truncate">{step.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default OnboardingStepNav;
